import type { UnitOpType } from '@/versions/v1/schema/unit-operation';

type UnitOpGlyphProps = {
  type: UnitOpType;
  className?: string;
  /** Render box edge length in px — default 16 (graph card slot). */
  size?: number;
  title?: string;
  'aria-hidden'?: boolean;
};

const GLYPH_PROPS = {
  viewBox: '0 0 16 16',
  fill: 'none',
  stroke: 'currentColor',
  strokeWidth: 1.5,
  strokeLinecap: 'round' as const,
  strokeLinejoin: 'round' as const,
};

/** Seed `UnitOpType` strings for `title` on graph glyphs. */
export const UNIT_OP_TYPE_LABEL: Record<UnitOpType, string> = {
  crusher: 'crusher',
  ball_mill: 'ball_mill',
  cyclone: 'cyclone',
  regrind: 'regrind',
  flotation: 'flotation',
  thickener: 'thickener',
  filter: 'filter',
  paste: 'paste',
};

function CrusherGlyph() {
  return (
    <svg {...GLYPH_PROPS} aria-hidden>
      <path d="M2 3 L6 7 L5 13" />
      <path d="M14 3 L10 7 L11 13" />
      <circle cx="8" cy="10" r="1.5" />
    </svg>
  );
}

function BallMillGlyph() {
  return (
    <svg {...GLYPH_PROPS} aria-hidden>
      <rect x="3" y="6" width="10" height="5" rx="2.5" />
      <path d="M3 8.5 L1.5 8.5" />
      <path d="M13 8.5 L14.5 8.5" />
      <path d="M14.5 8.5 L15.5 10.5" />
    </svg>
  );
}

function CycloneGlyph() {
  return (
    <svg {...GLYPH_PROPS} aria-hidden>
      <path d="M4 6 L2 5" />
      <path d="M8 3.5 L8 2" />
      <path d="M8 3.5 L12 7 L10 13.5 L6 13.5 L4 7 Z" />
    </svg>
  );
}

function RegrindGlyph() {
  return (
    <svg {...GLYPH_PROPS} aria-hidden>
      <rect x="1" y="7" width="6" height="3.5" rx="1.75" />
      <path d="M1 8.75 L0 8.75" />
      <path d="M10 5.5 L12.5 7.5 L11.5 12 L8.5 12 L7.5 7.5 Z" />
      <path d="M10 5.5 L10 4.5" />
    </svg>
  );
}

function FlotationGlyph() {
  return (
    <svg {...GLYPH_PROPS} aria-hidden>
      <rect x="2" y="5" width="12" height="9" rx="1" />
      <path d="M2 5.5 L14 5.5" />
      <path d="M4 9 L5.5 11 L7 9" />
      <path d="M7 9 L8.5 11 L10 9" />
      <path d="M10 9 L11.5 11 L13 9" />
    </svg>
  );
}

function ThickenerGlyph() {
  return (
    <svg {...GLYPH_PROPS} aria-hidden>
      <path d="M2 9 L14 9" />
      <path d="M3 9 L3 11 L13 11 L13 9" />
      <path d="M8 9.5 L8 11.5" />
      <path d="M6 11.5 L10 11.5" />
    </svg>
  );
}

function FilterGlyph() {
  return (
    <svg {...GLYPH_PROPS} aria-hidden>
      <rect x="3" y="4" width="2" height="9" rx="0.3" />
      <rect x="11" y="4" width="2" height="9" rx="0.3" />
      <rect x="5.5" y="6.5" width="5" height="4" rx="0.3" />
      <path d="M8 10.5 L8 13" />
    </svg>
  );
}

function PasteGlyph() {
  return (
    <svg {...GLYPH_PROPS} aria-hidden>
      <path d="M3 12 L3 7 L13 7 L13 12 Z" />
      <path d="M8 12 L8 9" />
      <path d="M6.5 9.5 L9.5 9.5" />
    </svg>
  );
}

export function UnitOpGlyph({ type, className, size = 16, title, 'aria-hidden': ariaHidden }: UnitOpGlyphProps) {
  const glyph = (() => {
    switch (type) {
      case 'crusher':
        return <CrusherGlyph />;
      case 'ball_mill':
        return <BallMillGlyph />;
      case 'cyclone':
        return <CycloneGlyph />;
      case 'regrind':
        return <RegrindGlyph />;
      case 'flotation':
        return <FlotationGlyph />;
      case 'thickener':
        return <ThickenerGlyph />;
      case 'filter':
        return <FilterGlyph />;
      case 'paste':
        return <PasteGlyph />;
    }
  })();

  return (
    <span
      className={className}
      title={title}
      aria-hidden={ariaHidden}
      data-unit-op-type={type}
      style={{ display: 'inline-flex', width: size, height: size, flexShrink: 0 }}
    >
      {glyph}
    </span>
  );
}
